// Service API pour les relevés d'index de compteurs
import axios from "axios";
import { CONFIG } from "../config";
import { getHeaders } from "./getHeaders";

/**
 * Récupérer tous les relevés d'index pour un device
 * @param {string} dev_eui - EUI du device
 * @param {number} limit - Nombre maximum de relevés à retourner (défaut: 100)
 * @returns {Promise<Array>} - Liste des relevés d'index
 */
export const fetchIndexReadings = async (dev_eui, limit = 100) => {
  try {
    let headers = await getHeaders();
    const response = await axios.get(
      `${CONFIG.API_URL}/devices/${dev_eui}/index-readings?limit=${limit}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération des relevés d'index:", error);
    if (error.response?.status === 422) {
      return [];
    }
    throw error;
  }
};

/**
 * Récupérer le dernier relevé d'index pour un device
 * @param {string} dev_eui - EUI du device
 * @returns {Promise<Object>} - Dernier relevé d'index
 */
export const fetchLatestIndexReading = async (dev_eui) => {
  try {
    let headers = await getHeaders();
    const response = await axios.get(
      `${CONFIG.API_URL}/devices/${dev_eui}/index-readings/latest`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération du dernier relevé:", error);
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Créer un nouveau relevé d'index
 * @param {string} dev_eui - EUI du device
 * @param {Object} data - Données du relevé
 * @param {number} data.index_value - Valeur de l'index
 * @param {string} data.reading_date - Date et heure du relevé (ISO 8601)
 * @param {string} data.reading_type - Type de relevé (MANUAL ou AUTO)
 * @param {string} data.source - Source du relevé
 * @param {string} data.notes - Notes optionnelles
 * @returns {Promise<Object>} - Relevé créé
 */
export const createIndexReading = async (dev_eui, data) => {
  try {
    let headers = await getHeaders();
    if (headers === null) {
      headers = {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        "Content-Type": "application/json",
      };
    }

    const response = await axios.post(
      `${CONFIG.API_URL}/devices/${dev_eui}/index-readings`,
      data,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création du relevé d'index:", error);
    throw error;
  }
};

/**
 * Mettre à jour un relevé d'index existant
 * @param {string} dev_eui - EUI du device
 * @param {number} reading_id - ID du relevé à modifier
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise<Object>} - Relevé mis à jour
 */
export const updateIndexReading = async (dev_eui, reading_id, data) => {
  try {
    let headers = await getHeaders();
    const response = await axios.put(
      `${CONFIG.API_URL}/devices/${dev_eui}/index-readings/${reading_id}`,
      data,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du relevé:", error);
    throw error;
  }
};

/**
 * Supprimer un relevé d'index (admin uniquement)
 * @param {string} dev_eui - EUI du device
 * @param {number} reading_id - ID du relevé à supprimer
 * @returns {Promise<void>}
 */
export const deleteIndexReading = async (dev_eui, reading_id) => {
  try {
    let headers = await getHeaders();
    const response = await axios.delete(
      `${CONFIG.API_URL}/devices/${dev_eui}/index-readings/${reading_id}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression du relevé:", error);
    throw error;
  }
};
